<script setup>
import { vMediaPoster, vMediaBackground } from '@drama/media-delivery/vue'
import { computed, inject, nextTick, onBeforeUnmount, onMounted, onUpdated, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Select } from '@drama/ui'
import SiteLoadingMark from './SiteLoadingMark.vue'
import VgIcon from './trpg/icons/VgIcon.vue'
import { videoSeriesHref } from '../lib/index.js'
import { useVideoLoadWindow } from '../composables/useVideoLoadWindow.js'

const api = inject('api')
if (!api) throw new Error('ShowcaseVideoList: api provider is required')

const props = defineProps({
  groups: { type: Array, required: true },
  dramaOptions: { type: Array, default: () => [] },
  showControls: { type: Boolean, default: false },
  initialFilters: { type: Object, default: () => ({}) },
  compact: { type: Boolean, default: false },
  compactSynopsis: { type: Boolean, default: false },
  verticalFeed: { type: Boolean, default: false },
  engagementActions: { type: Boolean, default: false },
  eagerPreload: { type: Boolean, default: false },
  mobileDramasNavigation: { type: Boolean, default: false },
  analyticsSurface: { type: String, default: '' },
})
const analytics = inject('analytics', null)
if (props.analyticsSurface && !analytics) throw new Error('ShowcaseVideoList: analytics provider is required when analyticsSurface is set')
const emit = defineEmits(['filters-changed'])
const { t, locale } = useI18n()
const mobileFeed = props.verticalFeed && window.matchMedia('(max-width: 680px)').matches
const hoverPreviewEnabled = !window.matchMedia('(max-width: 680px)').matches
const showcaseRoot = ref(null)
const shareNotice = ref('')
const engagementByVideoId = ref(new Map())
const engagementLoadingVideoIds = new Set()
const engagementBusyVideoIds = ref(new Set())
const engagementDesiredState = new Map()
const engagementRunningActions = new Set()
const expandedSynopsisIds = ref(new Set())
const overflowingSynopsisIds = ref(new Set())
const truncatedSynopsisById = ref(new Map())
const audibleVideoId = ref('')
const feedSoundEnabled = ref(false)
const loadingVideoIds = ref(new Set())
const userPausedVideoIds = ref(new Set())
const feedVideoIds = ref(new Set())
const feedPriorityVideoIds = ref(new Set())
const activeFeedVideoId = ref('')
const previewRequestedVideoIds = ref(new Set())
const loadWindow = useVideoLoadWindow()
const synopsisElements = new Map()
const synopsisObserver = typeof ResizeObserver === 'undefined'
  ? null
  : new ResizeObserver(entries => {
      for (const entry of entries) {
        const itemId = entry.target.dataset.synopsisId
        if (itemId) measureSynopsis(itemId)
      }
    })
const query = ref('')
const sort = ref('popular')
const dramaFilter = ref('all')
const publishedWithin = ref('all')
const dramaFilterOptions = computed(() => {
  return [
    { value: 'all', label: t('visualGame.landingCard.scriptAll') },
    ...props.dramaOptions,
  ]
})
const publishedWithinOptions = computed(() => [
  { value: 'all', label: t('visualGame.landingCard.publishedAll') },
  { value: 'today', label: t('visualGame.landingCard.publishedToday') },
  { value: 'week', label: t('visualGame.landingCard.publishedWeek') },
  { value: 'month', label: t('visualGame.landingCard.publishedMonth') },
])
const filteredGroups = computed(() => props.groups.map(group => ({
  ...group,
  items: sortItems(group.items.filter(matchesFilters)),
})).filter(group => group.items.length > 0))
const itemCount = computed(() => filteredGroups.value.reduce((total, group) => total + group.items.length, 0))
let feedObserver = null
let engagementObserver = null
let shareNoticeTimer = null
let recentFeedVideoIds = []
let feedPlaybackRequestId = 0
const previewDurationSeconds = 8
const recentFeedCacheSize = 5
const mobilePlaybackBufferSeconds = 3
const loadingDelayMs = 200
const loadingTimers = new Map()

onMounted(() => {
  applyInitialFilters()
  emitFiltersChanged()
  if (props.verticalFeed) void nextTick(initializeFeedObserver)
  if (props.engagementActions) void nextTick(initializeEngagementObserver)
  if (props.eagerPreload && !mobileFeed) void nextTick(observeFeedCards)
})

watch([query, sort, dramaFilter, publishedWithin], emitFiltersChanged)
onUpdated(observeFeedCards)
onBeforeUnmount(() => {
  synopsisObserver?.disconnect()
  feedObserver?.disconnect()
  engagementObserver?.disconnect()
  if (shareNoticeTimer !== null) window.clearTimeout(shareNoticeTimer)
  for (const timer of loadingTimers.values()) window.clearTimeout(timer)
  loadingTimers.clear()
})

async function initializeFeedObserver() {
  if (!mobileFeed) return
  const feedScroller = showcaseRoot.value.querySelector('.game-grid')
  const initialCards = [...feedScroller.querySelectorAll('.game-card')]
  const requestedVideoId = props.initialFilters.videoId
  const requestedIndex = typeof requestedVideoId === 'string'
    ? initialCards.findIndex(card => requiredCardVideoId(card) === requestedVideoId)
    : -1
  const initialIndex = requestedIndex < 0 ? 0 : requestedIndex
  updateFeedCache(initialCards, initialIndex)
  if (initialIndex > 0) feedScroller.scrollTop = initialCards[initialIndex].offsetTop
  await nextTick()
  feedObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.7) {
        void activateFeedCard(entry.target)
        continue
      }
      entry.target.querySelector('video').pause()
    }
  }, { root: feedScroller, threshold: [0, 0.7] })
  observeFeedCards()
}

async function activateFeedCard(card) {
  const cards = [...card.parentElement.querySelectorAll('.game-card')]
  const activeIndex = cards.indexOf(card)
  if (activeIndex < 0) throw new Error('Showcase active feed card is missing from its grid')
  const activeVideoId = requiredCardVideoId(card)
  activeFeedVideoId.value = activeVideoId
  setUserPaused(activeVideoId, false)
  if (feedSoundEnabled.value) audibleVideoId.value = activeVideoId
  const playbackRequestId = ++feedPlaybackRequestId
  updateFeedCache(cards, activeIndex)
  await nextTick()
  await startVideo(card.querySelector('video'), playbackRequestId)
}

function updateFeedCache(cards, activeIndex) {
  const activeVideoId = requiredCardVideoId(cards[activeIndex])
  recentFeedVideoIds = [activeVideoId, ...recentFeedVideoIds.filter(videoId => videoId !== activeVideoId)]
    .slice(0, recentFeedCacheSize)
  const priorityVideoIds = cards
    .slice(activeIndex, activeIndex + 3)
    .map(card => requiredCardVideoId(card))
  const nearbyVideoIds = cards
    .slice(Math.max(0, activeIndex - 1), activeIndex + 3)
    .map(card => requiredCardVideoId(card))
  feedPriorityVideoIds.value = new Set(priorityVideoIds)
  feedVideoIds.value = new Set([...recentFeedVideoIds, ...nearbyVideoIds])
}

function requiredCardVideoId(card) {
  const videoId = card.dataset.videoId
  if (!videoId) throw new Error('Showcase feed card video id is missing')
  return videoId
}

function videoSource(item) {
  if (mobileFeed) return feedVideoIds.value.has(item.video.id) ? item.video.source_url : undefined
  const requested = previewRequestedVideoIds.value.has(item.video.id)
  if (props.eagerPreload) return (requested || loadWindow.isVideoLoaded(item.video.id)) ? item.video.source_url : undefined
  return hoverPreviewEnabled && requested ? item.video.source_url : undefined
}

function videoPreload(item) {
  if (mobileFeed) {
    if (feedPriorityVideoIds.value.has(item.video.id)) return 'auto'
    return feedVideoIds.value.has(item.video.id) ? 'metadata' : 'none'
  }
  const requested = previewRequestedVideoIds.value.has(item.video.id)
  if (props.eagerPreload) return (requested || loadWindow.isVideoLoaded(item.video.id)) ? 'auto' : 'metadata'
  return hoverPreviewEnabled && requested ? 'auto' : 'none'
}

function requestVideoPreview(item) {
  if (previewRequestedVideoIds.value.has(item.video.id)) return
  previewRequestedVideoIds.value = new Set(previewRequestedVideoIds.value).add(item.video.id)
}

async function startVideo(video, playbackRequestId = null) {
  const videoId = videoIdFromElement(video)
  setVideoLoading(videoId, true)
  try {
    if (mobileFeed) {
      await waitForPlaybackBuffer(video, mobilePlaybackBufferSeconds)
      if (playbackRequestId !== null && (playbackRequestId !== feedPlaybackRequestId || activeFeedVideoId.value !== videoId)) {
        setVideoLoading(videoId, false)
        return
      }
    }
    await video.play()
  } catch {
    setVideoLoading(videoId, false)
  }
}

function waitForPlaybackBuffer(video, minimumSeconds) {
  if (playbackBufferIsReady(video, minimumSeconds)) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const events = ['canplay', 'canplaythrough', 'loadeddata', 'progress']
    const cleanup = () => {
      for (const event of events) video.removeEventListener(event, check)
      video.removeEventListener('error', fail)
    }
    const check = () => {
      if (!playbackBufferIsReady(video, minimumSeconds)) return
      cleanup()
      resolve()
    }
    const fail = () => {
      cleanup()
      reject(new Error(`Showcase video failed before buffering: ${videoIdFromElement(video)}`))
    }
    for (const event of events) video.addEventListener(event, check)
    video.addEventListener('error', fail, { once: true })
  })
}

function playbackBufferIsReady(video, minimumSeconds) {
  const remainingSeconds = Number.isFinite(video.duration) ? video.duration - video.currentTime : minimumSeconds
  return playbackBufferSeconds(video) >= Math.min(minimumSeconds, remainingSeconds)
}

function playbackBufferSeconds(video) {
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (video.buffered.start(index) > video.currentTime + 0.1) continue
    if (video.buffered.end(index) < video.currentTime) continue
    return video.buffered.end(index) - video.currentTime
  }
  return 0
}

function videoIdFromElement(video) {
  const videoId = video.closest('.game-card').dataset.videoId
  if (!videoId) throw new Error('Showcase preview video id is missing')
  return videoId
}

function setVideoLoading(videoId, loading) {
  const pendingTimer = loadingTimers.get(videoId)
  if (pendingTimer !== undefined) {
    window.clearTimeout(pendingTimer)
    loadingTimers.delete(videoId)
  }
  if (loading) {
    const timer = window.setTimeout(() => {
      loadingTimers.delete(videoId)
      updateLoadingVideoIds(videoId, true)
    }, loadingDelayMs)
    loadingTimers.set(videoId, timer)
    return
  }
  updateLoadingVideoIds(videoId, false)
}

function updateLoadingVideoIds(videoId, loading) {
  const next = new Set(loadingVideoIds.value)
  if (loading) next.add(videoId)
  else next.delete(videoId)
  loadingVideoIds.value = next
}

function updateVideoLoading(event, loading) {
  setVideoLoading(videoIdFromElement(event.currentTarget), loading)
}

function updateVideoReady(event) {
  if (mobileFeed && event.currentTarget.paused) return
  updateVideoLoading(event, false)
}

function videoIsLoading(videoId) {
  return loadingVideoIds.value.has(videoId)
}

function videoIsUserPaused(videoId) {
  return userPausedVideoIds.value.has(videoId)
}

function setUserPaused(videoId, paused) {
  const next = new Set(userPausedVideoIds.value)
  if (paused) next.add(videoId)
  else next.delete(videoId)
  userPausedVideoIds.value = next
}

function handleVideoPlaying(event) {
  const videoId = videoIdFromElement(event.currentTarget)
  setVideoLoading(videoId, false)
  setUserPaused(videoId, false)
}

function observeFeedCards() {
  const cards = [...showcaseRoot.value.querySelectorAll('.game-card')]
  if (props.eagerPreload && !mobileFeed) loadWindow.observeCards(null, cards)
  for (const card of cards) {
    feedObserver?.observe(card)
    engagementObserver?.observe(card)
  }
}

function initializeEngagementObserver() {
  engagementObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue
      const videoId = entry.target.dataset.videoId
      if (!videoId) throw new Error('Showcase engagement card is missing video id')
      void loadEngagement(videoId)
    }
  }, { threshold: 0.1 })
  observeFeedCards()
}

async function loadEngagement(videoId) {
  if (engagementByVideoId.value.has(videoId) || engagementLoadingVideoIds.has(videoId)) return
  engagementLoadingVideoIds.add(videoId)
  try {
    const engagement = await api.visualGame.getVideoEngagement(videoId)
    if (!engagementIsBusy(videoId)) setEngagement(videoId, engagement)
  } catch (reason) {
    showShareNotice(reason instanceof Error ? reason.message : String(reason))
  } finally {
    engagementLoadingVideoIds.delete(videoId)
  }
}

function setEngagement(videoId, engagement) {
  const next = new Map(engagementByVideoId.value)
  next.set(videoId, engagement)
  engagementByVideoId.value = next
}

function engagementIsBusy(videoId) {
  return engagementBusyVideoIds.value.has(videoId)
}

function engagementIsActive(videoId, field) {
  const engagement = engagementByVideoId.value.get(videoId)
  return engagement ? engagement[field] : false
}

function engagementCount(video, field) {
  const engagement = engagementByVideoId.value.get(video.id)
  return engagement ? engagement[field] : video[field]
}

function toggleEngagement(videoId, action) {
  const optimistic = toggledEngagement(currentEngagement(videoId), action)
  setEngagement(videoId, optimistic)
  engagementDesiredState.set(engagementActionKey(videoId, action), engagementActionState(optimistic, action))
  void reconcileEngagement(videoId, action)
}

async function reconcileEngagement(videoId, action) {
  const actionKey = engagementActionKey(videoId, action)
  if (engagementRunningActions.has(actionKey)) return
  engagementRunningActions.add(actionKey)
  markEngagementBusy(videoId)
  try {
    while (true) {
      const engagement = await requestEngagementToggle(videoId, action)
      const desiredState = engagementDesiredState.get(actionKey)
      if (desiredState === undefined) throw new Error(`Showcase engagement desired state is missing: ${actionKey}`)
      if (engagementActionState(engagement, action) !== desiredState) continue
      setEngagement(videoId, mergeEngagementResult(currentEngagement(videoId), engagement, action))
      engagementDesiredState.delete(actionKey)
      return
    }
  } catch (reason) {
    engagementDesiredState.delete(actionKey)
    setEngagement(videoId, await api.visualGame.getVideoEngagement(videoId))
    showShareNotice(reason instanceof Error ? reason.message : String(reason))
  } finally {
    engagementRunningActions.delete(actionKey)
    markEngagementIdle(videoId)
  }
}

function requestEngagementToggle(videoId, action) {
  switch (action) {
    case 'like':
      return api.visualGame.toggleVideoLike(videoId)
    case 'favorite':
      return api.visualGame.toggleVideoFavorite(videoId)
    default:
      throw new Error(`Unhandled showcase engagement action: ${action}, video: ${videoId}`)
  }
}

function engagementActionKey(videoId, action) {
  return `${videoId}:${action}`
}

function engagementActionState(engagement, action) {
  switch (action) {
    case 'like':
      return engagement.is_liked
    case 'favorite':
      return engagement.is_favorited
    default:
      throw new Error(`Unhandled showcase engagement state action: ${action}, video: ${engagement.video_id}`)
  }
}

function markEngagementBusy(videoId) {
  engagementBusyVideoIds.value = new Set([...engagementBusyVideoIds.value, videoId])
}

function markEngagementIdle(videoId) {
  if ([...engagementRunningActions].some(actionKey => actionKey.startsWith(`${videoId}:`))) return
  const next = new Set(engagementBusyVideoIds.value)
  next.delete(videoId)
  engagementBusyVideoIds.value = next
}

function currentEngagement(videoId) {
  const loaded = engagementByVideoId.value.get(videoId)
  if (loaded) return loaded
  for (const group of props.groups) {
    const item = group.items.find(candidate => candidate.video.id === videoId)
    if (!item) continue
    return {
      video_id: videoId,
      play_count: item.video.play_count,
      like_count: item.video.like_count,
      favorite_count: item.video.favorite_count,
      is_liked: false,
      is_favorited: false,
    }
  }
  throw new Error(`Showcase engagement video is missing: ${videoId}`)
}

function toggledEngagement(current, action) {
  switch (action) {
    case 'like':
      return {
        ...current,
        like_count: current.like_count + (current.is_liked ? -1 : 1),
        is_liked: !current.is_liked,
      }
    case 'favorite':
      return {
        ...current,
        favorite_count: current.favorite_count + (current.is_favorited ? -1 : 1),
        is_favorited: !current.is_favorited,
      }
    default:
      throw new Error(`Unhandled showcase engagement action: ${action}, video: ${current.video_id}`)
  }
}

function mergeEngagementResult(current, result, action) {
  switch (action) {
    case 'like':
      return {
        ...current,
        play_count: result.play_count,
        like_count: result.like_count,
        is_liked: result.is_liked,
      }
    case 'favorite':
      return {
        ...current,
        play_count: result.play_count,
        favorite_count: result.favorite_count,
        is_favorited: result.is_favorited,
      }
    default:
      throw new Error(`Unhandled showcase engagement result action: ${action}, video: ${current.video_id}`)
  }
}

function applyInitialFilters() {
  if (typeof props.initialFilters.query === 'string') query.value = props.initialFilters.query
  if (props.initialFilters.sort === 'latest' || props.initialFilters.sort === 'popular') sort.value = props.initialFilters.sort
  if (typeof props.initialFilters.drama === 'string' && props.initialFilters.drama.length > 0) dramaFilter.value = props.initialFilters.drama
  if (['all', 'today', 'week', 'month'].includes(props.initialFilters.published)) publishedWithin.value = props.initialFilters.published
}

function emitFiltersChanged() {
  emit('filters-changed', {
    query: query.value,
    sort: sort.value,
    drama: dramaFilter.value,
    published: publishedWithin.value,
  })
}

function matchesFilters(item) {
  if (dramaFilter.value !== 'all' && dramaFilterId(item) !== dramaFilter.value) return false
  const normalizedQuery = query.value.trim().toLocaleLowerCase(locale.value)
  if (normalizedQuery) {
    const searchText = item.searchText.toLocaleLowerCase(locale.value)
    if (!searchText.includes(normalizedQuery)) return false
  }
  const publishedAt = Date.parse(item.video.completed_at)
  if (!Number.isFinite(publishedAt)) throw new Error(`Showcase video completed_at is invalid: ${item.video.id}`)
  switch (publishedWithin.value) {
    case 'all':
      return true
    case 'today': {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      return publishedAt >= start.getTime()
    }
    case 'week':
      return publishedAt >= Date.now() - 7 * 24 * 60 * 60 * 1000
    case 'month':
      return publishedAt >= Date.now() - 30 * 24 * 60 * 60 * 1000
    default:
      throw new Error(`Unhandled showcase published filter: ${publishedWithin.value}`)
  }
}

function sortItems(items) {
  if (!props.showControls) return [...items]
  switch (sort.value) {
    case 'latest':
      return [...items].sort((left, right) => Date.parse(right.video.completed_at) - Date.parse(left.video.completed_at))
    case 'popular':
      return [...items].sort((left, right) => right.video.play_count - left.video.play_count || Date.parse(right.video.completed_at) - Date.parse(left.video.completed_at))
    default:
      throw new Error(`Unhandled showcase sort: ${sort.value}`)
  }
}

function clearFilters() {
  query.value = ''
  dramaFilter.value = 'all'
  publishedWithin.value = 'all'
}

function dramaFilterId(item) {
  return item.scenario.series_id === null ? `scenario:${item.scenario.id}` : `series:${item.scenario.series_id}`
}

function toggleSynopsis(itemId) {
  const next = new Set(expandedSynopsisIds.value)
  if (next.has(itemId)) next.delete(itemId)
  else next.add(itemId)
  expandedSynopsisIds.value = next
  void nextTick(() => measureSynopsis(itemId))
}

function bindSynopsisElement(itemId, synopsis, element) {
  const previous = synopsisElements.get(itemId)
  if (previous && previous !== element) synopsisObserver?.unobserve(previous)
  if (!element) {
    synopsisElements.delete(itemId)
    return
  }
  element.dataset.synopsisId = itemId
  element.dataset.fullSynopsis = synopsis
  synopsisElements.set(itemId, element)
  synopsisObserver?.observe(element)
  void nextTick(() => measureSynopsis(itemId))
}

function measureSynopsis(itemId) {
  if (expandedSynopsisIds.value.has(itemId)) return
  const element = synopsisElements.get(itemId)
  if (!element) return
  const synopsis = element.dataset.fullSynopsis
  if (!synopsis) throw new Error(`Showcase synopsis is missing: ${itemId}`)
  const truncated = fitSynopsisText(element, synopsis)
  const isOverflowing = truncated !== null
  if (overflowingSynopsisIds.value.has(itemId) !== isOverflowing) {
    const nextOverflowing = new Set(overflowingSynopsisIds.value)
    if (isOverflowing) nextOverflowing.add(itemId)
    else nextOverflowing.delete(itemId)
    overflowingSynopsisIds.value = nextOverflowing
  }
  if (truncated === null && !truncatedSynopsisById.value.has(itemId)) return
  if (truncated !== null && truncatedSynopsisById.value.get(itemId) === truncated) return
  const nextTruncated = new Map(truncatedSynopsisById.value)
  if (truncated === null) nextTruncated.delete(itemId)
  else nextTruncated.set(itemId, truncated)
  truncatedSynopsisById.value = nextTruncated
}

function fitSynopsisText(element, synopsis) {
  if (element.clientWidth <= 0 || element.clientHeight <= 0) return null
  const hasRenderedOverflow = element.scrollHeight > element.clientHeight + 0.5
  const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight)
  if (!Number.isFinite(lineHeight)) throw new Error(`Showcase synopsis line height is invalid: ${element.dataset.synopsisId}`)
  const maxHeight = lineHeight * 3 + 0.5
  const probe = element.cloneNode(false)
  probe.style.position = 'fixed'
  probe.style.top = '0'
  probe.style.left = '0'
  const measurementSafetyWidth = 12
  probe.style.width = `${element.clientWidth - measurementSafetyWidth}px`
  probe.style.minHeight = '0'
  probe.style.maxHeight = 'none'
  probe.style.overflow = 'visible'
  probe.style.visibility = 'hidden'
  probe.style.pointerEvents = 'none'
  probe.style.display = 'block'
  probe.style.webkitLineClamp = 'unset'
  element.parentElement.append(probe)
  probe.textContent = synopsis
  if (probe.scrollHeight <= maxHeight && !hasRenderedOverflow) {
    probe.remove()
    return null
  }

  const expandButton = document.createElement('button')
  expandButton.type = 'button'
  expandButton.textContent = t('visualGame.landingCard.expandSynopsis')
  expandButton.style.display = 'inline'
  expandButton.style.margin = '0 0 0 3px'
  expandButton.style.padding = '0'
  expandButton.style.border = '0'
  expandButton.style.font = 'inherit'
  expandButton.style.lineHeight = 'inherit'

  let low = 0
  let high = synopsis.length
  while (low < high) {
    const middle = Math.ceil((low + high) / 2)
    probe.replaceChildren(document.createTextNode(`${synopsis.slice(0, middle).trimEnd()}…`), expandButton)
    if (probe.scrollHeight <= maxHeight) low = middle
    else high = middle - 1
  }
  probe.remove()
  return synopsis.slice(0, low).trimEnd()
}

function showcaseVideoHref(item) {
  const seriesId = item.scenario.series_id === null ? `scenario:${item.scenario.id}` : item.scenario.series_id
  return videoSeriesHref(
    item.scenario.id,
    seriesId,
    item.video.id,
    item.video.season_id === null ? '' : item.video.season_id,
  )
}

function showcaseCardHref(item) {
  if (props.mobileDramasNavigation && window.matchMedia('(max-width: 680px)').matches) {
    return `/dramas?${new URLSearchParams({ videoId: item.video.id }).toString()}`
  }
  return showcaseVideoHref(item)
}

function playerHref(playerId) {
  return `/players/${encodeURIComponent(playerId)}`
}

function showShareNotice(message) {
  shareNotice.value = message
  if (shareNoticeTimer !== null) window.clearTimeout(shareNoticeTimer)
  shareNoticeTimer = window.setTimeout(() => {
    shareNotice.value = ''
    shareNoticeTimer = null
  }, 2400)
}

async function shareVideo(item) {
  const share = {
    title: item.title,
    url: new URL(showcaseVideoHref(item), window.location.origin).href,
  }
  try {
    if (navigator.share) {
      await navigator.share(share)
      return
    }
    await navigator.clipboard.writeText(share.url)
    showShareNotice(t('visualGame.videoCenter.shareCopied'))
  } catch (reason) {
    if (reason instanceof DOMException && reason.name === 'AbortError') return
    showShareNotice(t('visualGame.videoCenter.shareFailed'))
  }
}

async function toggleVideoSound(item, event) {
  const video = event.currentTarget.closest('.game-card').querySelector('video')
  const enableSound = audibleVideoId.value !== item.video.id
  if (mobileFeed) feedSoundEnabled.value = enableSound
  audibleVideoId.value = enableSound ? item.video.id : ''
  await nextTick()
  video.muted = !enableSound
  if (!enableSound || !video.paused) return
  try {
    setVideoLoading(item.video.id, true)
    await video.play()
  } catch (reason) {
    setVideoLoading(item.video.id, false)
    if (mobileFeed) feedSoundEnabled.value = false
    audibleVideoId.value = ''
    video.muted = true
    showShareNotice(reason instanceof Error ? reason.message : String(reason))
  }
}

async function playPreview(event, item) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  requestVideoPreview(item)
  await nextTick()
  const video = event.currentTarget.querySelector('video')
  resetPreviewIfOutsideRange(video)
  await startVideo(video)
}

function initializePreview(event) {
  const video = event.currentTarget
  video.dataset.previewStart = String(previewStartSeconds(video))
  video.currentTime = Number(video.dataset.previewStart)
}

function previewStartSeconds(video) {
  if (mobileFeed) return 0
  const videoId = videoIdFromElement(video)
  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    throw new Error(`Showcase preview duration is invalid: ${videoId}`)
  }
  if (video.duration <= 10) return 0
  if (video.duration <= 18) return video.duration * 0.25

  const ratio = 0.35 + stableVideoHash(videoId) % 31 / 100
  const preferredStart = video.duration * ratio
  const latestStart = video.duration - previewDurationSeconds - 5
  return Math.min(Math.max(preferredStart, 5), latestStart)
}

function stableVideoHash(videoId) {
  let hash = 0
  for (const character of videoId) hash = (hash * 31 + character.codePointAt(0)) >>> 0
  return hash
}

function resetPreviewIfOutsideRange(video) {
  const previewStart = Number(video.dataset.previewStart)
  if (!Number.isFinite(previewStart)) return
  if (video.currentTime < previewStart || video.currentTime >= previewStart + previewDurationSeconds) {
    video.currentTime = previewStart
  }
}

function limitPreview(event) {
  if (mobileFeed) {
    updateVideoLoading(event, false)
    return
  }
  const video = event.currentTarget
  const previewStart = Number(video.dataset.previewStart)
  if (!Number.isFinite(previewStart)) return
  if (video.currentTime < previewStart + previewDurationSeconds) return
  video.pause()
  video.currentTime = previewStart
}

function stopPreview(event) {
  const video = event.currentTarget.querySelector('video')
  video.pause()
  const previewStart = Number(video.dataset.previewStart)
  if (Number.isFinite(previewStart)) video.currentTime = previewStart
}

async function openShowcaseVideo(event) {
  if (!mobileFeed) return
  event.preventDefault()
  const video = event.currentTarget.closest('.game-card').querySelector('video')
  const videoId = videoIdFromElement(video)
  if (!video.paused) {
    video.pause()
    setVideoLoading(videoId, false)
    setUserPaused(videoId, true)
    return
  }
  await startVideo(video)
}

function openTrackedShowcaseVideo(event, item, position) {
  if (props.analyticsSurface) {
    analytics.track('content_click', {
      content_type: 'drama',
      content_id: item.video.id,
      content_title: item.title,
      surface: props.analyticsSurface,
      position,
      destination: showcaseCardHref(item),
    })
  }
  return openShowcaseVideo(event)
}
</script>

<template>
  <section ref="showcaseRoot" class="showcase-video-list" :class="{ 'is-compact': compact, 'is-vertical-feed': verticalFeed }">
    <div v-if="showControls" class="showcase-controls">
      <label>
        <span class="sr-only">{{ t('visualGame.landing.searchLabel') }}</span>
        <input v-model="query" type="search" :placeholder="t('visualGame.landingCard.searchPlaceholder')" />
      </label>
      <label class="script-filter">
        <span>{{ t('visualGame.landingCard.scriptFilter') }}</span>
        <span class="script-filter-select">
          <Select
            size="sm"
            mobile-sheet
            :aria-label="t('visualGame.landingCard.scriptFilter')"
            :model-value="dramaFilter"
            :options="dramaFilterOptions"
            @update:model-value="dramaFilter = $event"
          />
        </span>
      </label>
      <label class="publish-filter">
        <span>{{ t('visualGame.landingCard.publishedAt') }}</span>
        <span class="publish-filter-select">
          <Select
            size="sm"
            mobile-sheet
            :aria-label="t('visualGame.landingCard.publishedAt')"
            :model-value="publishedWithin"
            :options="publishedWithinOptions"
            @update:model-value="publishedWithin = $event"
          />
        </span>
      </label>
      <div class="sort-tabs" :aria-label="t('visualGame.landing.sortLabel')">
        <button type="button" :class="{ 'is-active': sort === 'popular' }" @click="sort = 'popular'">{{ t('visualGame.landing.popular') }}</button>
        <button type="button" :class="{ 'is-active': sort === 'latest' }" @click="sort = 'latest'">{{ t('visualGame.landing.latest') }}</button>
      </div>
    </div>

    <div v-if="itemCount > 0" class="showcase-results">
    <section v-for="group in filteredGroups" :key="group.id" class="showcase-result-group">
      <header v-if="group.playerName" class="player-search-result">
        <span class="player-search-avatar" aria-hidden="true">{{ group.playerName.trim().slice(0, 1).toUpperCase() }}</span>
        <div>
          <a :href="playerHref(group.playerId)">{{ group.playerName }}</a>
          <span>{{ t('visualGame.landingCard.playerVideoCount', { count: group.items.length }) }}</span>
        </div>
      </header>
      <div class="game-grid">
        <article
          v-for="(item, itemIndex) in group.items"
          :key="item.id"
          class="game-card"
          :data-video-id="item.video.id"
          @mouseenter="playPreview($event, item)"
          @mouseleave="stopPreview"
          @focusin="playPreview($event, item)"
          @focusout="stopPreview"
        >
          <a class="game-cover" :href="showcaseCardHref(item)" @click="event => openTrackedShowcaseVideo(event, item, itemIndex + 1)">
            <span
              v-if="verticalFeed && item.video.poster_url !== null"
              class="game-cover-backdrop"
              v-media-background="item.video.poster_url" style="background-image: var(--media-image)"
              aria-hidden="true"
            ></span>
            <video
              class="game-cover-main"
              :src="videoSource(item)"
              v-media-poster="item.video.poster_url"
              :muted="audibleVideoId !== item.video.id"
              :loop="mobileFeed"
              playsinline
              :preload="videoPreload(item)"
              @loadedmetadata="initializePreview"
              @canplay="updateVideoReady"
              @playing="handleVideoPlaying"
              @waiting="updateVideoLoading($event, true)"
              @stalled="updateVideoLoading($event, true)"
              @error="updateVideoLoading($event, false)"
              @timeupdate="limitPreview"
            ></video>
            <span v-if="mobileFeed && videoIsUserPaused(item.video.id)" class="game-cover-paused" aria-hidden="true">
              <VgIcon name="play" :size="34" />
            </span>
            <Transition name="game-cover-loading">
              <span v-if="videoIsLoading(item.video.id)" class="game-cover-loading" role="status" :aria-label="t('visualGame.status.loading')">
                <SiteLoadingMark />
              </span>
            </Transition>
          </a>
          <button
            v-if="verticalFeed"
            class="game-card-sound"
            type="button"
            :aria-label="t(audibleVideoId === item.video.id ? 'player.mute' : 'player.unmute')"
            @click="toggleVideoSound(item, $event)"
          >
            <VgIcon :name="audibleVideoId === item.video.id ? 'volume-high' : 'volume-mute'" :size="22" />
          </button>
          <div class="game-card-copy">
            <h3>
              <a :href="showcaseCardHref(item)" @click="event => openTrackedShowcaseVideo(event, item, itemIndex + 1)">
                {{ item.title }}
              </a>
            </h3>
            <div v-if="!compact" class="game-card-context">
              <span>{{ item.dramaTitle }}</span>
              <a v-if="item.video.player_id !== null" :href="playerHref(item.video.player_id)">{{ item.video.player_name }}</a>
              <span v-else>{{ item.video.player_name }}</span>
            </div>
            <div v-else class="game-card-compact-meta">
              <span class="game-card-player-avatar" aria-hidden="true">{{ item.video.player_name.trim().slice(0, 1).toUpperCase() }}</span>
              <a v-if="item.video.player_id !== null" :href="playerHref(item.video.player_id)">{{ item.video.player_name }}</a>
              <span v-else>{{ item.video.player_name }}</span>
              <span class="game-card-compact-plays">· {{ item.video.play_count }} {{ t('visualGame.landingMetrics.playCount') }}</span>
            </div>
            <div v-if="!compact || compactSynopsis" class="game-card-synopsis">
              <p
                v-if="!compact"
                :ref="element => bindSynopsisElement(item.id, item.synopsis, element)"
                :class="{ 'is-expanded': expandedSynopsisIds.has(item.id) }"
              >
                <template v-if="expandedSynopsisIds.has(item.id)">{{ item.synopsis }}</template>
                <template v-else-if="overflowingSynopsisIds.has(item.id)">{{ truncatedSynopsisById.get(item.id) }}…<button type="button" :aria-expanded="false" @click="toggleSynopsis(item.id)">{{ t('visualGame.landingCard.expandSynopsis') }}</button></template>
                <template v-else>{{ item.synopsis }}</template>
              </p>
              <p
                v-else
                :ref="element => bindSynopsisElement(item.id, item.synopsis, element)"
                :class="{ 'is-expanded': expandedSynopsisIds.has(item.id) }"
              >
                <template v-if="expandedSynopsisIds.has(item.id)">{{ item.synopsis }}</template>
                <template v-else-if="overflowingSynopsisIds.has(item.id)">{{ truncatedSynopsisById.get(item.id) }}…<button type="button" :aria-expanded="false" @click="toggleSynopsis(item.id)">{{ t('visualGame.landingCard.expandSynopsis') }}</button></template>
                <template v-else>{{ item.synopsis }}</template>
              </p>
              <button
                v-if="!compact && overflowingSynopsisIds.has(item.id) && expandedSynopsisIds.has(item.id)"
                type="button"
                :aria-expanded="true"
                @click="toggleSynopsis(item.id)"
              >
                {{ t('visualGame.landingCard.collapseSynopsis') }}
              </button>
              <button
                v-if="compact && overflowingSynopsisIds.has(item.id) && expandedSynopsisIds.has(item.id)"
                type="button"
                :aria-expanded="true"
                @click="toggleSynopsis(item.id)"
              >
                {{ t('visualGame.landingCard.collapseSynopsis') }}
              </button>
            </div>
            <p v-if="!compact" class="game-card-meta">
              <span class="game-card-metric" :aria-label="`${t('visualGame.landingMetrics.playCount')} ${item.video.play_count}`" :data-label="t('visualGame.landingMetrics.playCount')"><VgIcon name="eye" :size="14" />{{ item.video.play_count }}</span>
              <span class="game-card-metric" :aria-label="`${t('visualGame.landingMetrics.likeCount')} ${item.video.like_count}`" :data-label="t('visualGame.landingMetrics.likeCount')"><VgIcon name="heart" :size="14" />{{ item.video.like_count }}</span>
              <time :datetime="String(item.video.completed_at)">{{ item.completedAtLabel }}</time>
            </p>
          </div>
          <button
            v-if="engagementActions"
            v-analytics-click="{ element_id: 'share_video', surface: 'player_stories', scenario_id: item.scenario.id }"
            class="game-card-share"
            type="button"
            :aria-label="`${t('visualGame.videoCenter.share')} ${item.title}`"
            @click="shareVideo(item)"
          >
            <VgIcon name="share-forward" :size="32" />
          </button>
          <div v-if="engagementActions" class="game-card-engagement">
            <button
              v-analytics-click="{ element_id: 'toggle_video_like', surface: 'player_stories', scenario_id: item.scenario.id }"
              type="button"
              :class="{ 'is-active': engagementIsActive(item.video.id, 'is_liked') }"
              :aria-busy="engagementIsBusy(item.video.id)"
              :aria-label="`${t('visualGame.videoCenter.like')} ${item.title}`"
              @click="toggleEngagement(item.video.id, 'like')"
            >
              <VgIcon name="heart" :size="28" />
              <span>{{ engagementCount(item.video, 'like_count') }}</span>
            </button>
            <button
              v-analytics-click="{ element_id: 'toggle_video_favorite', surface: 'player_stories', scenario_id: item.scenario.id }"
              type="button"
              :class="{ 'is-active': engagementIsActive(item.video.id, 'is_favorited') }"
              :aria-busy="engagementIsBusy(item.video.id)"
              :aria-label="`${t('visualGame.videoCenter.save')} ${item.title}`"
              @click="toggleEngagement(item.video.id, 'favorite')"
            >
              <VgIcon name="star" :size="28" />
              <span>{{ engagementCount(item.video, 'favorite_count') }}</span>
            </button>
          </div>
        </article>
      </div>
    </section>
    </div>
    <div v-else class="showcase-empty">
      <VgIcon name="search" :size="28" />
      <h3>{{ t('visualGame.landing.noSearchResults') }}</h3>
      <button type="button" @click="clearFilters">{{ t('visualGame.landing.clearSearch') }}</button>
    </div>
    <p v-if="shareNotice" class="share-notice" role="status">{{ shareNotice }}</p>
  </section>
</template>

<style scoped>
.showcase-video-list { display: grid; gap: var(--drama-space-2xl); }
.showcase-controls { display: flex; align-items: center; gap: var(--drama-space-lg); padding-bottom: var(--drama-space-xl); border-bottom: 1px solid var(--drama-color-border); }
.showcase-controls label { flex: 1; }
.showcase-controls input { width: min(100%, 420px); height: 44px; padding: 0 var(--drama-space-lg); border: 1px solid var(--drama-color-border); background: var(--drama-color-bg); color: var(--drama-color-text); }
.showcase-controls .script-filter,
.showcase-controls .publish-filter { display: flex; flex: 0 0 auto; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-text-muted); font-size: 12px; }
.script-filter-select { min-width: 180px; }
.publish-filter-select { min-width: 150px; }
.script-filter-select :deep(.drama-select),
.publish-filter-select :deep(.drama-select) { width: 100%; height: 44px; border-color: var(--drama-color-border); border-radius: 0; background: var(--drama-color-bg); }
.sort-tabs { display: flex; gap: var(--drama-space-xs); }
.sort-tabs button { height: 44px; padding: 0 var(--drama-space-lg); border: 1px solid transparent; background: transparent; color: var(--drama-color-text-muted); cursor: pointer; }
.sort-tabs button.is-active { border-color: var(--drama-color-primary); color: var(--drama-color-primary); }
.showcase-results, .showcase-result-group { display: grid; gap: var(--drama-space-2xl); }
.showcase-result-group + .showcase-result-group { padding-top: var(--drama-space-2xl); border-top: 1px solid var(--drama-color-border); }
.player-search-result { display: flex; align-items: center; gap: var(--drama-space-lg); }
.player-search-avatar { display: grid; width: 58px; height: 58px; flex: 0 0 auto; place-items: center; border: 1px solid var(--drama-color-primary); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-primary) 12%, var(--drama-color-bg)); color: var(--drama-color-primary); font-family: var(--drama-font-family-heading); font-size: var(--drama-font-size-lg); }
.player-search-result > div { display: grid; gap: 5px; }
.player-search-result div a { color: var(--drama-color-text); font-size: var(--drama-font-size-lg); font-weight: var(--drama-font-weight-medium); text-decoration: none; }
.player-search-result div a:hover { color: var(--drama-color-primary); }
.player-search-result div span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.game-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--drama-space-lg); }
.game-card { position: relative; display: block; min-width: 0; color: var(--drama-color-text); }
.game-card-sound { display: none; }
.game-card-share { position: absolute; z-index: 3; top: 10px; right: 10px; display: inline-flex; min-width: 36px; height: 36px; align-items: center; justify-content: center; gap: 5px; padding: 0 10px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-surface) 90%, transparent); color: var(--drama-color-text); box-shadow: var(--drama-shadow-sm); cursor: pointer; }
.game-card-engagement { position: absolute; z-index: 3; top: 54px; right: 10px; display: grid; gap: 6px; }
.game-card-engagement button { display: grid; width: 36px; min-height: 36px; place-items: center; gap: 2px; padding: 5px 0; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-surface) 90%, transparent); color: var(--drama-color-text); box-shadow: var(--drama-shadow-sm); cursor: pointer; }
.game-card-engagement button span { font-size: 9px; line-height: 1; }
.game-card-engagement button.is-active { background: var(--drama-color-primary); color: var(--drama-color-on-primary); }
.game-card-engagement button.is-active .vg-icon { fill: currentColor; }
.share-notice { position: fixed; z-index: 90; bottom: calc(24px + env(safe-area-inset-bottom)); left: 50%; margin: 0; padding: 9px 14px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-text); box-shadow: var(--drama-shadow-md); font-size: 12px; white-space: nowrap; transform: translateX(-50%); }
.game-cover { position: relative; display: block; aspect-ratio: 16 / 9; overflow: hidden; border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); text-decoration: none; }
.game-cover video { display: block; width: 100%; height: 100%; object-fit: cover; transition: transform .3s ease, filter .3s ease; }
.game-cover-loading { position: absolute; z-index: 2; inset: 0; display: grid; place-items: center; background: color-mix(in srgb, var(--drama-color-text) 34%, transparent); pointer-events: none; --drama-color-primary: var(--drama-color-on-primary); }
.game-cover-loading :deep(.site-loading-mark) { transform: scale(.72); }
.game-cover-paused { position: absolute; z-index: 2; top: 50%; left: 50%; display: grid; width: 64px; height: 64px; place-items: center; border: 1px solid color-mix(in srgb, var(--drama-color-bg) 30%, transparent); border-radius: var(--drama-radius-full); background: color-mix(in srgb, var(--drama-color-text) 58%, transparent); color: var(--drama-color-bg); box-shadow: var(--drama-shadow-md); pointer-events: none; transform: translate(-50%, -50%); }
.game-cover-loading-enter-active, .game-cover-loading-leave-active { transition: opacity 160ms ease; }
.game-cover-loading-enter-from, .game-cover-loading-leave-to { opacity: 0; }
.game-card:hover .game-cover video, .game-card:focus-visible .game-cover video { filter: brightness(1.06); transform: scale(1.02); }
.game-card-copy { min-width: 0; padding-top: var(--drama-space-md); }
.game-card-copy h3 { display: -webkit-box; overflow: hidden; margin: 0 0 7px; font-family: var(--drama-font-family-body); font-size: 15px; font-weight: var(--drama-font-weight-medium); line-height: 1.4; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.game-card-copy h3 a { color: var(--drama-color-text); text-decoration: none; }
.game-card-copy h3 a:hover { color: var(--drama-color-primary); }
.game-card-context { display: flex; overflow: hidden; align-items: center; justify-content: space-between; gap: var(--drama-space-md); margin: 0 0 6px; color: var(--drama-color-text-muted); font-size: 12px; line-height: 1.4; white-space: nowrap; }
.game-card-context span, .game-card-context a { overflow: hidden; text-overflow: ellipsis; }
.game-card-context span:first-child { color: var(--drama-color-text); font-weight: var(--drama-font-weight-medium); }
.game-card-context > :last-child { margin-left: auto; }
.game-card-context a { color: var(--drama-color-text-muted); text-decoration: none; }
.game-card-context a:hover { color: var(--drama-color-primary); }
.game-card-synopsis { position: relative; margin-bottom: var(--drama-space-sm); }
.game-card-synopsis p { display: -webkit-box; min-height: 4.2em; max-height: 4.2em; overflow: hidden; margin: 0; color: var(--drama-color-text-muted); font-size: 11px; line-height: 1.4; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.game-card-synopsis p.is-expanded { display: block; min-height: 0; max-height: none; overflow: visible; -webkit-line-clamp: unset; }
.game-card-synopsis button { margin-top: 4px; padding: 0; border: 0; background: transparent; color: var(--drama-color-primary); font-size: 10px; cursor: pointer; }
.game-card-synopsis p button { display: inline; margin: 0 0 0 3px; font-size: 11px; line-height: inherit; vertical-align: baseline; }
.game-card-meta { display: flex; align-items: center; gap: var(--drama-space-md); margin: 0; color: var(--drama-color-text-muted); font-size: 11px; line-height: 1.4; }
.game-card-meta time { margin-left: auto; text-align: right; }
.game-card-metric { position: relative; display: inline-flex; align-items: center; gap: 4px; }
.is-compact .showcase-results, .is-compact .showcase-result-group { gap: var(--drama-space-xl); }
.is-compact .game-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: clamp(14px, 1.5vw, 20px); }
.is-compact .game-cover { border-radius: 10px; }
.is-compact .game-card-copy { padding-top: 8px; }
.is-compact .game-card-copy h3 { overflow: hidden; margin-bottom: 6px; font-size: 14px; font-weight: var(--drama-font-weight-semibold); line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; -webkit-line-clamp: 1; }
.is-compact .game-card-synopsis { margin-block: 7px 0; }
.is-compact .game-card-synopsis p { min-height: 4.35em; max-height: 4.35em; -webkit-line-clamp: 3; }
.is-compact .game-card-synopsis p.is-expanded { display: block; min-height: 0; max-height: none; overflow: visible; -webkit-line-clamp: unset; }
.game-card-compact-meta { display: flex; min-width: 0; align-items: center; gap: 6px; color: var(--drama-color-text-muted); font-size: 11px; line-height: 22px; }
.game-card-player-avatar { display: grid; width: 20px; height: 20px; flex: 0 0 20px; place-items: center; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text); font-size: 9px; font-weight: var(--drama-font-weight-semibold); }
.game-card-compact-meta > a,
.game-card-compact-meta > span:not(.game-card-player-avatar):not(.game-card-compact-plays) { overflow: hidden; color: var(--drama-color-text-muted); text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
.game-card-compact-meta > a:hover { color: var(--drama-color-text); }
.game-card-compact-plays { flex: 0 0 auto; white-space: nowrap; }
.showcase-empty { display: grid; min-height: 260px; place-items: center; align-content: center; gap: var(--drama-space-md); color: var(--drama-color-text-muted); text-align: center; }
.showcase-empty h3 { margin: 0; color: var(--drama-color-text); }
.showcase-empty button { padding: var(--drama-space-sm) var(--drama-space-lg); border: 1px solid var(--drama-color-primary); background: transparent; color: var(--drama-color-primary); cursor: pointer; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
@media (min-width: 681px) {
  .is-compact .game-card { display: grid; grid-template-columns: minmax(0, 1fr) auto auto; }
  .is-compact .game-cover, .is-compact .game-card-copy { grid-column: 1 / -1; }
  .is-compact .game-card-copy { grid-row: 2; }
  .is-compact .game-card-compact-meta { padding-right: 112px; }
  .is-compact .game-card-share { position: relative; top: auto; right: auto; grid-column: 2; grid-row: 2; width: 28px; min-width: 28px; height: 22px; align-self: start; margin-top: 33px; padding: 0; border: 0; border-radius: 0; background: transparent; box-shadow: none; }
  .is-compact .game-card-share :deep(.vg-icon) { width: 17px !important; height: 17px !important; }
  .is-compact .game-card-engagement { position: relative; top: auto; right: auto; display: flex; grid-column: 3; grid-row: 2; align-self: start; gap: 3px; margin-top: 33px; }
  .is-compact .game-card-engagement button { display: inline-flex; width: auto; min-width: 34px; min-height: 22px; align-items: center; justify-content: center; gap: 3px; padding: 0 3px; border: 0; border-radius: 0; background: transparent; box-shadow: none; }
  .is-compact .game-card-engagement button :deep(.vg-icon) { width: 16px !important; height: 16px !important; }
  .is-compact .game-card-engagement button.is-active { background: transparent; color: var(--drama-color-primary); }
  .is-compact .game-card-engagement button span { font-size: 9px; }
}
@media (max-width: 980px) { .game-grid, .is-compact .game-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 981px) and (max-width: 1280px) { .game-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 680px) { .showcase-controls { align-items: stretch; flex-direction: column; } .showcase-controls .script-filter, .showcase-controls .publish-filter { align-items: stretch; flex-direction: column; } .script-filter-select, .publish-filter-select { width: 100%; } .game-grid, .is-compact .game-grid { grid-template-columns: 1fr; } .share-notice { bottom: calc(112px + env(safe-area-inset-bottom)); } }
</style>
